import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import * as Highcharts from 'highcharts';
import { Options, SeriesOptionsType } from 'highcharts';
import { WidgetEntryWithData, WidgetWithData } from '@models/widget/widget.model';
import { SPANISH_SHORT_MONTHS } from '../highcharts-lang-es.constant';

@Component({
  standalone: false,
  selector: 'app-widget-bar-chart',
  templateUrl: './widget-bar-chart.component.html',
  styleUrls: ['./widget-bar-chart.component.scss'],
})
export class WidgetBarChartComponent implements OnChanges {
  @Input({ required: true }) widget!: WidgetWithData;
  @Input() isPreview = false;
  @Input() showChrome = false;
  @Input() typeLabel = '';
  @Input() sourceLabel = '';
  @Input() isEditable = false;
  @Input() canDelete = true;
  @Input() isEditMode = false;
  @Input() isMockPreview = false;
  @Input() sensorTypeLabels: string[] = [];

  @Output() edit   = new EventEmitter<void>();
  @Output() remove = new EventEmitter<void>();

  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Options = {};
  updateFlag = false;
  private readonly defaultDecimals = 1;
  private cap(s: string): string { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

  constructor() {
    Highcharts.setOptions({
      time: { useUTC: true },
      lang: { shortMonths: SPANISH_SHORT_MONTHS },
    } as any);
  }

  ngOnChanges(): void {
    this.updateFlag = false;
    this.chartOptions = this.buildChartOptions();
    this.updateFlag = true;
  }

  private buildChartOptions(): Options {
    const decimals = this.defaultDecimals;

    const series: SeriesOptionsType[] = this.widget.entries.map((serie) => {
      const isLine = serie.visualizationType === 'LINE';
      if (isLine) {
        const data = this.toChartData(serie);
        return {
          type:      'line',
          name:      this.cap(serie.label || serie.fieldName || 'Serie'),
          color:     serie.color || '#3B82F6',
          data,
          marker:    { enabled: data.length === 1, radius: data.length === 1 ? 4 : 2.5, symbol: 'circle' },
          lineWidth: 2.2,
          custom:    { unit: serie.unit ?? '' },
        } as SeriesOptionsType;
      }
      return {
        type:         'column',
        name:         serie.label || serie.fieldName || 'Serie',
        color:        serie.color || '#3B82F6',
        data:         this.toChartData(serie),
        borderWidth:  0,
        borderRadius: 4,
        custom:       { unit: serie.unit ?? '' },
      } as SeriesOptionsType;
    });

    return {
      accessibility: { enabled: false },
      chart: { backgroundColor: 'transparent', spacing: [8, 8, 4, 6], animation: !this.isPreview && !this.isMockPreview },
      title: { text: undefined },
      credits: { enabled: false },
      legend: {
        enabled: !this.isPreview && series.length > 0,
        align: 'left',
        verticalAlign: 'top',
        itemDistance: 12,
        symbolWidth: 8,
        symbolHeight: 8,
        itemMarginTop: 0,
        itemMarginBottom: 0,
        itemStyle: { color: '#64748b', fontSize: '10px', fontWeight: '500' },
      },
      xAxis: {
        type: 'datetime',
        labels: { enabled: !this.isPreview, style: { color: '#64748b', fontSize: '10px' } },
        lineColor: '#cbd5e1',
        tickColor: '#cbd5e1',
        tickLength: 4,
        dateTimeLabelFormats: {
          millisecond: '%H:%M',
          second:      '%H:%M',
          minute:      '%H:%M',
          hour:        '%H:%M',
          day:         '%b %e',
          week:        '%b %e',
          month:       '%b %Y',
          year:        '%Y',
        },
      },
      yAxis: {
        title: { text: undefined },
        labels: {
          style: { color: '#a0aab8', fontSize: '10px' },
          format: '{value}',
        },
        gridLineColor: '#f4f7fb',
        gridLineDashStyle: 'Dash',
      },
      tooltip: {
        enabled: !this.isPreview,
        shared: true,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formatter(this: any): string {
          const pts = this.points ?? [];
          const dateStr = Highcharts.dateFormat('%d/%m/%Y %H:%M', this.x as number);
          const rows = pts.map(p => {
            const unit = (p.series.userOptions.custom as Record<string, string>)?.['unit'] ?? '';
            const val  = p.y != null ? (p.y as number).toFixed(decimals) : '--';
            return `<span style="color:${String(p.color)}">●</span> ${p.series.name}: <b>${val}${unit ? ' ' + unit : ''}</b>`;
          });
          return `<small style="color:#6b7280">${dateStr}</small><br>${rows.join('<br>')}`;
        },
      },
      exporting: { enabled: false },
      plotOptions: {
        column: { pointPadding: 0.12, groupPadding: 0.14, borderWidth: 0 },
        series: {
          animation: !this.isPreview && !this.isMockPreview,
          states: { inactive: { opacity: 1 }, hover: { lineWidthPlus: 0 } },
        },
      },
      series,
    };
  }

  get hasNoData(): boolean {
    return (this.widget.entries ?? []).every(s => !s.data?.length);
  }

  get timeRangeLabel(): string {
    const map: Record<string, string> = {
      '1h':  'Última hora',
      '6h':  'Últimas 6 horas',
      '24h': 'Últimas 24 horas',
      '7d':  'Últimos 7 días',
      '30d': 'Últimos 30 días',
    };
    const tr = this.widget.config?.timeRange;
    return tr ? (map[tr] ?? tr) : '';
  }

  private toChartData(serie: WidgetEntryWithData): Array<[number, number]> {
    return (serie.data ?? []).map((point) => [new Date(point.date).getTime(), Number(point.value)]);
  }
}
